import { Box, Tooltip, Typography, IconButton } from '@material-ui/core'
import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CreateIcon from '@mui/icons-material/Create'
import ChatMessageArea from 'src/components/chatbot/components/ChatMessageArea'
import ChatThreadsButton from 'src/components/chatbot/components/ChatThreadsButton'

const CHAT_HEIGHT = '80vh'
const MIN_CHAT_HEIGHT = 400
const MAX_CHAT_HEIGHT = 800

const CHAT_WIDTH = '40vw'
const MIN_CHAT_WIDTH = 300
const MAX_CHAT_WIDTH = 500

const ChatContainer = styled(motion.div)`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 1000;
  width: ${CHAT_WIDTH};
  min-width: ${MIN_CHAT_WIDTH}px;
  max-width: ${MAX_CHAT_WIDTH}px;
  height: ${CHAT_HEIGHT};
  min-height: ${MIN_CHAT_HEIGHT}px;
  max-height: ${MAX_CHAT_HEIGHT}px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.customShadows.z16};
  background-color: ${({ theme }) => theme.palette.background.paper};
  overflow: hidden;
`

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`

const HeaderLogo = styled.img`
  width: 20px;
  height: 20px;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`

export default function ChatInterface({
  onClose,
  onNewChat,
  onHPOButtonClick,
  onDiseaseButtonClick,
  onSamplesButtonClick,
  onPanelsButtonClick,
  isDarkMode,
  isVariantsTablePage,
  suggestedQueries,
  selectedHPOTerms,
  onRemoveHPOTerm,
  selectedDiseases,
  onRemoveDisease,
  selectedSamples,
  onRemoveSample,
  selectedPanels,
  onRemovePanel,
}) {
  const logoSrc = '/static/brand/minimal_logo.png'

  const panelWidth = Math.min(window.innerWidth * 0.4, 500)
  const panelHeight = Math.min(window.innerHeight * 0.8, 800)

  // Always position the chat widget in the bottom right corner
  // regardless of button position
  const panelStyle = { bottom: 16, right: 16, position: 'fixed' }

  return (
    <ChatContainer
      initial={{ opacity: 0, height: 0, y: 20 }}
      animate={{ opacity: 1, height: CHAT_HEIGHT, y: 0 }}
      exit={{ opacity: 0, height: 0, y: 20 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      }}
      isDarkMode={isDarkMode}
      style={panelStyle}
    >
      <ChatHeader>
        <HeaderLeft>
          <HeaderLogo src={logoSrc} alt="logo" />
          <Typography variant="subtitle1">
            Genesus<sup style={{ fontWeight: 'normal', fontSize: '0.5em' }}>TM</sup>{' '}
            <Box
              component="span"
              sx={{
                fontSize: '0.75em',
                fontStyle: 'italic',
                position: 'relative',
                top: '0.15em',
              }}
            >
              Assistant
            </Box>
          </Typography>
        </HeaderLeft>
        <HeaderActions>
          <Tooltip title="New Chat" placement="bottom">
            <IconButton size="medium" onClick={onNewChat}>
              <CreateIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <ChatThreadsButton />
          <IconButton size="medium" onClick={onClose}>
            <ExpandMoreIcon fontSize="medium" />
          </IconButton>
        </HeaderActions>
      </ChatHeader>

      <ChatMessageArea
        isDarkMode={isDarkMode}
        isVariantsTablePage={isVariantsTablePage}
        suggestedQueries={suggestedQueries}
        onHPOButtonClick={onHPOButtonClick}
        onDiseaseButtonClick={onDiseaseButtonClick}
        onSamplesButtonClick={onSamplesButtonClick}
        onPanelsButtonClick={onPanelsButtonClick}
        selectedHPOTerms={selectedHPOTerms}
        onRemoveHPOTerm={onRemoveHPOTerm}
        selectedDiseases={selectedDiseases}
        onRemoveDisease={onRemoveDisease}
        selectedSamples={selectedSamples}
        onRemoveSample={onRemoveSample}
        selectedPanels={selectedPanels}
        onRemovePanel={onRemovePanel}
      />
    </ChatContainer>
  )
}
